import Vue from 'vue';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import relativeTime from 'dayjs/plugin/relativeTime';
import { axios, blueApi } from '@/axios.config';
import { mockAdapter } from '@/mocks/axiosMockAdapter.config';
import { isFinite, percent } from '@/lib/numbers';
import { sortSpecifyOrder } from '@/lib/sorter';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(relativeTime);

/**
 * Hash
 * @Param {string} id - account id, group id, cross tag id
 * @Param {string} level - 'account' or 'acctgroup'
 * @Param {string} vendor - Cloud vendor
 * @Param {string} from - From date - optional
 * @Param {string} to - To date - optional
 *
 */
export const hash = ({ id, level, vendor, from, to }) => {
  const date = () => {
    if (from && to) {
      return `-${from}-${to}-`;
    } else if (from && !to) {
      return `-${from}-`;
    } else if (!from && to) {
      return `-${to}-`;
    } else {
      return `-`;
    }
  };
  return `${id}${date()}${level}-${vendor}`;
};

export const state = {
  monthlyRange: {
    start: null,
    end: null,
  },
  monthly: {},
  monthlyLatestUpdateTime: {},
  monthToDate: {},
  monthToDateBudget: {},
  monthToDateLatestUpdateTime: {},
  monthOnMonth: {},
  monthOnMonthLatestUpdateTime: {},

  loading: {
    fetchInitial: false,
    fetchMonthly: false,
    fetchMonthToDate: false,
    fetchMonthOnMonth: false,
  },

  cancelToken: {
    fetchMonthly: null,
    fetchMonthToDate: null,
    fetchMonthOnMonth: null,
  },
  error: null,
};

export const actions = {
  fetchForecastMonthly({ state, commit, getters, rootState, rootGetters }, { force = false }) {
    const methodName = 'fetchMonthly';

    if (!force) {
      if (state.monthly[getters.reqHash]) return false;
    }

    // Axios Cancel
    if (state.cancelToken[methodName]) {
      state.cancelToken[methodName].cancel();
    }
    // Axios Cancel - Token
    const cancelToken = new axios.CancelToken.source();
    const token = cancelToken.token;

    // Fetch期間
    let start, end;
    // Budget termがある場合。
    // 前3ヶ月、後6ヶ月を追加
    const budgetTerm = rootGetters['budget/getBudgetTerm'];
    if (budgetTerm.length === 2) {
      start = dayjs(budgetTerm[0])
        .subtract(3, 'month')
        .format('YYYYMM');
      end = dayjs(budgetTerm[1])
        .add(3, 'month')
        .format('YYYYMM');
    } else {
      // Budget termがない場合。
      start = dayjs()
        .startOf('year')
        .format('YYYYMM');
      end = dayjs()
        .add(3, 'month')
        .format('YYYYMM');
    }

    const id = rootState.usageReports.id;
    const level = rootGetters['usageReports/getLevel'];
    const vendor = rootState.vendor;

    const reqHash = hash({ id: id, level: level, vendor: vendor });

    const url = `cost/v1/${vendor}/forecasts/monthly/${level}/${id}`;
    const date = {
      fromDate: start,
      toDate: end,
    };

    mockAdapter(blueApi, 'get', url, 'getCostForecastMonthly', 200);

    commit('fetchStart', { method: methodName, reqHash: reqHash, cancelToken: cancelToken });

    return blueApi
      .get(url, { params: date, cancelToken: token })
      .then(res => {
        commit('updateForecastMonthly', { data: res.data, hash: reqHash, date: date });
        commit('fetchSucceed', { method: methodName });

        return Promise.resolve();
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          console.log('Request canceled', id);
        } else {
          if (err.response.status === 501) {
            return Promise.resolve('Forecast API has problem');
          }

          commit('fetchFailed', { method: methodName });
          throw err;
        }
      });
  },

  fetchForecastMonthToDate({ state, commit, getters, rootState, rootGetters }, { force = false }) {
    const methodName = 'fetchMonthToDate';

    if (!force) {
      if (state.monthToDate[getters.reqHash]) return false;
    }

    // Axios Cancel
    if (state.cancelToken[methodName]) {
      state.cancelToken[methodName].cancel();
    }
    // Axios Cancel - Token
    const cancelToken = new axios.CancelToken.source();
    const token = cancelToken.token;

    const id = rootState.usageReports.id;
    const level = rootGetters['usageReports/getLevel'];
    const vendor = rootState.vendor;

    let reqHash = hash({ id: id, level: level, vendor: vendor });

    const url = `cost/v1/${vendor}/forecasts/month-to-date/${level}/${id}`;

    mockAdapter(blueApi, 'get', url, 'getCostForecastMonthToDate', 200);

    commit('fetchStart', { method: methodName, cancelToken: cancelToken });

    return blueApi
      .get(url, { cancelToken: token })
      .then(res => {
        commit('updateForecastMonthToDate', { data: res.data, hash: reqHash });
        commit('fetchSucceed', { method: methodName });
        return Promise.resolve();
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          console.log('Request canceled', id);
        } else {
          if (err.response.status === 501) {
            return Promise.resolve('Forecast API has problem');
          }

          commit('fetchFailed', { method: methodName });
          throw err;
        }
      });
  },

  fetchForecastMonthOnMonth({ state, commit, getters, rootState, rootGetters }, { force = false }) {
    const methodName = 'fetchMonthOnMonth';

    if (!force) {
      if (state.monthOnMonth[getters.reqHash]) return false;
    }

    // Axios Cancel
    if (state.cancelToken[methodName]) {
      state.cancelToken[methodName].cancel();
    }
    // Axios Cancel - Token
    const cancelToken = new axios.CancelToken.source();
    const token = cancelToken.token;

    const id = rootState.usageReports.id;
    const level = rootGetters['usageReports/getLevel'];
    const vendor = rootState.vendor;

    let reqHash = hash({ id: id, level: level, vendor: vendor });

    const url = `cost/v1/${vendor}/forecasts/month-on-month/${level}/${id}`;

    mockAdapter(blueApi, 'get', url, 'getCostForecastMonthOnMonth', 200);

    commit('fetchStart', { method: methodName, cancelToken: cancelToken });

    return blueApi
      .get(url, { cancelToken: token })
      .then(res => {
        commit('updateForecastMonthOnMonth', { data: res.data, hash: reqHash });
        commit('fetchSucceed', { method: methodName });
        return Promise.resolve();
      })
      .catch(err => {
        if (axios.isCancel(err)) {
          console.log('Request canceled', id);
        } else {
          if (err.response.status === 501) {
            return Promise.resolve('Forecast API has problem');
          }

          commit('fetchFailed', { method: methodName });
          throw err;
        }
      });
  },
};

export const mutations = {
  fetchStart(state, { method, cancelToken }) {
    state['loading'][method] = true;
    state.cancelToken[method] = cancelToken;
  },

  fetchSucceed(state, { method }) {
    state['loading'][method] = false;
    state.cancelToken[method] = null;
  },

  fetchFailed(state, { method }) {
    state['loading'][method] = false;
    state.cancelToken[method] = null;
  },

  updateFetchInitial(state, { method, val }) {
    state['loading'][method] = val;
  },

  updateForecastMonthly(state, { data, hash }) {
    if (!data.data.length) {
      Vue.set(state.monthly, hash, []);
      return false;
    }

    // Change upperBound and lowerBound data - [Temporary - 2021/07/26]
    // Forecastの±20%の値に変更
    //data.data.forEach(i => {
    //  if (i.upperBound > 0) {
    //    i.upperBound = i.costForecast + i.costForecast * 0.2;
    //    i.lowerBound = i.costForecast - i.costForecast * 0.2;
    //  }
    //});

    // Check data
    const checkedData = data.data.filter(i => {
      if (!i.date || i.date === '') return false;
      else return true;
    });

    Vue.set(state.monthly, hash, checkedData);
    Vue.set(state.monthlyLatestUpdateTime, hash, data.timestamp);
  },

  updateForecastMonthToDate(state, { data, hash }) {
    // Check data
    const checkedData = data.data.filter(i => {
      if (!i.date || i.date === '') return false;
      else return true;
    });

    Vue.set(state.monthToDate, hash, checkedData);
    Vue.set(state.monthToDateBudget, hash, data.budget);
    Vue.set(state.monthToDateLatestUpdateTime, hash, data.timestamp);
  },

  updateForecastMonthOnMonth(state, { data, hash }) {
    // Check data
    const checkedData = data.data.filter(i => {
      if (!i.category || i.category === '') return false;
      else return true;
    });

    Vue.set(state.monthOnMonth, hash, checkedData);
    Vue.set(state.monthOnMonthLatestUpdateTime, hash, data.timestamp);
  },
};

export const getters = {
  reqHash(state, getters, rootState, rootGetters) {
    const id = rootState.usageReports.id;
    const level = rootGetters['usageReports/getLevel'];
    const vendor = rootState.vendor;

    return hash({ id: id, level: level, vendor: vendor });
  },
  /**
   * All chart data - monthly
   * @param state
   * @param getters
   * @return { array }
   */
  getChartDataMonthlyAll(state, getters) {
    const targetHash = getters.reqHash;

    const data = state.monthly[targetHash] ? state.monthly[targetHash] : null;

    if (!data) return [];
    if (!data.length) return [];

    const format = data => {
      const arr = [];

      data.forEach(i => {
        const obj = {
          date: i.date,
          cost: i.costActual,
          forecast: i.costForecast,
          budget: i.budget,
          upperBound: i.upperBound,
          lowerBound: i.lowerBound,
        };

        // cost - Add current month cost.
        // Using getSummaryMonthToDateTotal.
        if (dayjs(i.date).isSame(dayjs(), 'month')) {
          // obj.cost = getters.getSummaryMonthToDateTotal ? getters.getSummaryMonthToDateTotal : 0;
          obj.cost = i.costActual;
        }

        // cost - Delete data from the next month onwards - 翌月移行のデータを消去
        // forecast, upperBound, lowerBound - Delete value is 0 data from the next month onwards - 翌月移行のデータで値が0を消去
        if (dayjs(i.date).isAfter(dayjs(), 'month')) {
          delete obj.cost;

          for (const objKey in obj) {
            if (obj[objKey] === 0) {
              delete obj[objKey];
            }
          }
        }

        // forecast, upperBound, lowerBound - Delete data before this month - 今月以前のデータを消去
        if (dayjs(i.date).isBefore(dayjs(), 'month')) {
          delete obj.forecast;
          delete obj.upperBound;
          delete obj.lowerBound;
        }

        arr.push(obj);
      });

      return arr;
    };

    return format(data);
  },

  /**
   * Latest update time of Monthly.
   */
  getChartDataMonthlyLatestUpdateTime(state, getters) {
    const targetHash = getters.reqHash;
    const data = state.monthlyLatestUpdateTime[targetHash]
      ? state.monthlyLatestUpdateTime[targetHash]
      : null;

    return data ? data : null;
  },

  /**
   * Month to Date.
   * Return current month and previous month data.
   * If no previous data in day, add 0 value to previous property.
   */
  getChartDataMonthToDate(state, getters) {
    const targetHash = getters.reqHash;
    const data = state.monthToDate[targetHash] ? state.monthToDate[targetHash] : null;

    if (!data) return [];
    if (!data.length) return [];

    const format = data => {
      const arr = [];

      data.forEach(i => {
        const obj = {
          date: i.date,
          cost: i.costAccumulated,
          forecast: i.costForecast,
          previous: i.costPrev,
          upperBound: i.upperBound,
          lowerBound: i.lowerBound > 0 ? i.lowerBound : 0,
        };

        // Temporary [2021/07/27]
        if (i.costAccumulated === 0) {
          delete obj.cost;
        }
        // Temporary [2021/07/27]
        if (i.costPrev === 0) {
          delete obj.previous;
        }

        arr.push(obj);
      });

      return arr;
    };

    return format(data);
  },

  /**
   * Latest update time of Month to Date.
   */
  getChartDataMonthToDateLatestUpdateTime(state, getters) {
    const targetHash = getters.reqHash;
    const data = state.monthToDateLatestUpdateTime[targetHash]
      ? state.monthToDateLatestUpdateTime[targetHash]
      : null;

    return data ? data : null;
  },

  getChartDataMonthToDateBudget(state, getters) {
    const targetHash = getters.reqHash;
    const data = state.monthToDateBudget[targetHash] ? state.monthToDateBudget[targetHash] : null;

    return data ? data : 0;
  },

  getChartDataMonthOnMonth(state, getters) {
    const targetHash = getters.reqHash;
    const data = state.monthOnMonth[targetHash] ? state.monthOnMonth[targetHash] : null;

    if (!data) return [];
    if (!data.length) return [];

    const format = data => {
      const arr = [];

      data.forEach(i => {
        const obj = {
          group: i.category,
          cost: i.costCurrent,
          forecast: i.costForecast,
          previous_cost: i.costPrev,
        };

        arr.push(obj);
      });

      // Sort
      const order = [
        'Compute',
        'Database',
        'Storage',
        'Networking',
        'Security',
        'Analytics',
        'ApplicationIntegration',
        'DeveloperTools',
        'ManagementGovernance',
        'Others',
      ];
      return sortSpecifyOrder(arr, 'group', order);
    };

    return format(data);
  },

  /**
   * Latest update time of Month on Month.
   */
  getChartDataMonthOnMonthLatestUpdateTime(state, getters) {
    const targetHash = getters.reqHash;
    const data = state.monthOnMonthLatestUpdateTime[targetHash]
      ? state.monthOnMonthLatestUpdateTime[targetHash]
      : null;

    return data ? data : null;
  },

  getChartDataMonthOnMonthCurrent(state) {
    const data = state.monthOnMonth;

    if (!data) return [];
    if (!data.length) return [];

    const target = data.find(i => i.id === 'current');

    return target ? target.chart_data : [];
  },

  getChartDataMonthOnMonthPrevious(state) {
    const data = state.monthOnMonth;

    if (!data) return [];
    if (!data.length) return [];

    const target = data.find(i => i.id === 'previous');

    return target ? target.chart_data : [];
  },

  /**
   * Summary
   */
  /**
   * Monthly
   */
  /**
   * Monthly - 現在までの利用額
   * 算出方法：予算開始月から今月までのCostActualの合計
   */
  getSummaryMonthlyTotal(state, getters, rootState, rootGetters) {
    // start date. (dayjs format)
    let start;
    const budgetStartDate = rootGetters['budget/getBudgetStartDate'];
    if (!budgetStartDate) {
      start = dayjs().startOf('year');
    } else {
      start = dayjs(budgetStartDate);
    }

    // Calculate
    let value = 0;
    const data = getters.getChartDataMonthlyAll;
    data.forEach(i => {
      if (dayjs(i.date).isSameOrAfter(start, 'month')) {
        value += i.cost ? i.cost : 0;
      }
    });

    return value;
  },

  /**
   * Monthly - 残りの期間の予測合計
   * 算出方法：来月からの予測の合計値
   */
  getSummaryMonthlyForecastTotal(state, getters) {
    const current = getters.getSummaryMonthlyTotal;
    const forecast = getters.getSummaryMonthlyRemaining;

    return current + forecast;
  },

  /**
   * Monthly - 残りの期間の予測合計
   * 算出方法：来月からの予測の合計値
   */
  getSummaryMonthlyRemaining(state, getters) {
    // Next month
    const nextMonth = dayjs().add(1, 'month');

    // Calculate
    let value = 0;
    const data = getters.getChartDataMonthlyAll;
    data.forEach(i => {
      if (dayjs(i.date).isSameOrAfter(nextMonth, 'month')) {
        value += i.forecast ? i.forecast : 0;
      }
    });

    return value;
  },

  /**
   * Monthly - 予算の残り
   * バジェット合計 - 現在の利用額合計
   */
  getSummaryMonthlyBudgetRemaining(state, getters, rootState, rootGetters) {
    const budgetTotal = rootGetters['budget/getBudgetTotal'];
    const accumulated = getters.getSummaryMonthlyTotal;

    return budgetTotal - accumulated;
  },

  /**
   * Monthly - 予算の進捗
   */
  getSummaryMonthlyBudgetProgress(state, getters, rootState, rootGetters) {
    const budgetTotal = rootGetters['budget/getBudgetTotal'];
    const accumulated = getters.getSummaryMonthlyTotal;

    // Calculate
    const calc = (budget, acc) => {
      let value;

      value = acc / budget;
      value = percent(value);

      return value;
    };

    const used = calc(budgetTotal, accumulated);

    return {
      used: used,
      remaining: 100 - used,
    };
  },

  /**
   * Daily
   */
  /**
   * Daily - 今日までの利用額
   * 算出方法：今日のcostAccumulatedの値
   */
  getSummaryMonthToDateTotal(state, getters) {
    const targetHash = getters.reqHash;
    const data = state.monthToDate[targetHash] ? state.monthToDate[targetHash] : null;
    if (!data) return 0;

    const latestUpdate = state.monthToDateLatestUpdateTime[targetHash]
      ? dayjs(state.monthToDateLatestUpdateTime[targetHash])
      : dayjs();

    const target = data.find(i => dayjs(i.date).isSame(latestUpdate, 'day'));

    return target ? target.costAccumulated : null;
  },

  /**
   * Daily - 利用総額の予測
   * 月末の予測額
   */
  getSummaryMonthToDateForecastTotal(state, getters) {
    const targetHash = getters.reqHash;
    const data = state.monthToDate[targetHash] ? state.monthToDate[targetHash] : null;
    if (!data) return 0;

    const target = data[data.length - 1];
    if (!target) return 0;

    return target.costForecast;
  },

  /**
   * Daily - 残り期間の予測
   * 予測合計 - 今月の利用額
   */
  getSummaryMonthToDateForecastRemaining(state, getters) {
    const accumulated = getters.getSummaryMonthToDateTotal;
    const forecast = getters.getSummaryMonthToDateForecastTotal;

    return forecast - accumulated;
  },

  /**
   * Daily - 予算の残り
   * バジェット - 今月の利用額合計
   */
  getSummaryMonthToDateBudgetRemaining(state, getters) {
    const budget = getters.getChartDataMonthToDateBudget;
    const accumulated = getters.getSummaryMonthToDateTotal;

    return budget - accumulated;
  },

  /**
   * Daily - 予算の進捗
   */
  getSummaryMonthToDateBudgetProgress(state, getters) {
    const budget = getters.getChartDataMonthToDateBudget;
    const accumulated = getters.getSummaryMonthToDateTotal;

    // Calculate
    const calc = (budget, acc) => {
      let value;
      value = acc / budget;

      if (!isFinite(value)) return 0;

      value = percent(value);

      return value;
    };

    const used = calc(budget, accumulated);

    return {
      used: used,
      remaining: 100 - used,
    };
  },
};
